import { TYPE_LABELS } from "@/lib/complaintTypes";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import KPICard from "@/components/KPICard";
import ComplaintStatusBadge from "@/components/ComplaintStatusBadge";
import ActionStatusBadge from "@/components/ActionStatusBadge";
import { nomConcerne } from "@/lib/concerned";
import SeverityBadge from "@/components/SeverityBadge";
import { api } from "@/lib/api";
import {
  AlertOctagon, CalendarClock, Clock3, AlarmClock, TrendingUp, ChevronRight, Loader2,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#1d4ed8", "#2563eb", "#0ea5e9", "#8b5cf6", "#06b6d4"];
const TYPE_COLORS = { client: "#1d4ed8", fournisseur: "#0ea5e9", interne: "#8b5cf6" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stats/dashboard");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmtMonth = (ym) => {
    const [y, m] = ym.split("-");
    const months = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
    return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
  };

  return (
    <>
      <Header title="Tableau de bord" subtitle="Vue opérationnelle qualité" />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 space-y-6">
        {loading || !data ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <KPICard
                label="Litiges ouverts"
                value={data.kpi.open_complaints}
                icon={AlertOctagon}
                accent="blue"
                hint={`${data.kpi.critical_complaints} critique(s)`}
                testId="kpi-open"
              />
              <KPICard
                label="Litiges du mois"
                value={data.kpi.month_complaints}
                icon={CalendarClock}
                accent="emerald"
                testId="kpi-month"
              />
              <KPICard
                label="Actions en retard"
                value={data.kpi.overdue_actions}
                icon={AlarmClock}
                accent="rose"
                hint="Échéance dépassée"
                testId="kpi-overdue"
              />
              <KPICard
                label="Délai moyen traitement"
                value={`${data.kpi.avg_resolution} j`}
                icon={Clock3}
                accent="amber"
                hint="Clôture moyenne"
                testId="kpi-avg"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 font-heading">
                      Litiges par mois
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">6 derniers mois</p>
                  </div>
                  <TrendingUp size={18} className="text-slate-400" strokeWidth={1.5} />
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.by_month.map(d => ({ ...d, month: fmtMonth(d.month) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12, border: "1px solid #e2e8f0",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06)", fontSize: 12,
                      }}
                    />
                    <Bar dataKey="count" fill="#1d4ed8" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
                <h3 className="text-lg font-semibold text-slate-900 font-heading mb-1">
                  Répartition
                </h3>
                <p className="text-xs text-slate-500 mb-4">Client / Fournisseur / Interne</p>
                {data.distribution.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={data.distribution}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        {data.distribution.map((entry) => (
                          <Cell key={entry.name} fill={TYPE_COLORS[entry.name] || "#94a3b8"} />
                        ))}
                      </Pie>
                      <Legend
                        formatter={(v) => TYPE_LABELS[v] || v}
                        wrapperStyle={{ fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-sm text-slate-400 py-12 text-center">Aucune donnée</div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
                <h3 className="text-lg font-semibold text-slate-900 font-heading mb-1">
                  Litiges par type
                </h3>
                <p className="text-xs text-slate-500 mb-4">Tous types confondus</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.by_type.map(d => ({ ...d, name: TYPE_LABELS[d.name] || d.name }))} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={80} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Bar dataKey="value" fill="#0ea5e9" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900 font-heading">Derniers litiges</h3>
                  <button
                    onClick={() => navigate("/litiges")}
                    data-testid="view-all-complaints"
                    className="text-sm font-semibold text-blue-700 hover:text-blue-800 flex items-center gap-1"
                  >
                    Tout voir <ChevronRight size={14} />
                  </button>
                </div>
                <div className="space-y-2">
                  {data.latest_complaints.length === 0 ? (
                    <div className="text-sm text-slate-400 py-8 text-center">Aucun litige</div>
                  ) : data.latest_complaints.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => navigate(`/litiges/${c.id}`)}
                      data-testid={`latest-complaint-${c.id}`}
                      className="w-full text-left p-3 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/40 transition-colors flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-slate-900 font-heading">{c.number}</span>
                          <ComplaintStatusBadge status={c.status} />
                          <SeverityBadge severity={c.severity} />
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {TYPE_LABELS[c.type]} · {nomConcerne(c)} · {c.description}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-slate-300" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 font-heading">Actions urgentes</h3>
                <button
                  onClick={() => navigate("/actions")}
                  data-testid="view-all-actions"
                  className="text-sm font-semibold text-blue-700 hover:text-blue-800 flex items-center gap-1"
                >
                  Toutes les actions <ChevronRight size={14} />
                </button>
              </div>
              {data.urgent_actions.length === 0 ? (
                <div className="text-sm text-slate-400 py-8 text-center">Aucune action urgente</div>
              ) : (
                <div className="space-y-2">
                  {data.urgent_actions.map((a) => (
                    <div
                      key={a.id}
                      className="p-3 rounded-xl border border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3"
                      data-testid={`urgent-action-${a.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-slate-900 font-heading">{a.number}</span>
                          <ActionStatusBadge status={a.status} />
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {a.action}
                        </div>
                      </div>
                      <div className="text-xs text-slate-500">
                        <span className="text-slate-400 mr-1">Échéance :</span>
                        <span className="font-semibold text-rose-600">{a.due_date?.slice(0, 10)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </>
  );
}
